import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function CIExplorer() {
  const [cis, setCis] = useState([]);
  const [search, setSearch] = useState("");
  const [impact, setImpact] = useState(null);
  const [classes, setClasses] = useState(null);
  const [draft, setDraft] = useState({ ci_class_key: "", name: "", attributes: {} });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadCis = () =>
    apiFetch("/cmdb/cis").then(async (r) => {
      if (!r.ok) { setError("Configuration items are not available to you"); return; }
      setCis(await r.json());
    });

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    loadCis();
    apiFetch("/cmdb/classes").then((r) => (r.ok ? r.json() : null)).then((body) => {
      setClasses(body);
      if (body?.classes?.length) {
        setDraft((d) => ({ ...d, ci_class_key: d.ci_class_key || body.classes[0].key }));
      }
    });
  }, [navigate]);

  const chosenClass = classes?.classes.find((c) => c.key === draft.ci_class_key);

  const authorCi = () =>
    apiFetch("/cmdb/author", {
      method: "POST",
      body: JSON.stringify({ ci_class_key: draft.ci_class_key, name: draft.name,
                             attributes: draft.attributes }),
    }).then(async (r) => {
      const out = await r.json();
      if (!r.ok) {
        setMessage(typeof out.detail === "string" ? out.detail : JSON.stringify(out.detail));
        return;
      }
      setMessage(out.message);
      setDraft({ ...draft, name: "", attributes: {} });
      loadCis();
      open(out.id);
    });

  const correct = (key, value) =>
    apiFetch(`/cmdb/cis/${impact.ci.id}/attributes`, {
      method: "PUT", body: JSON.stringify({ attributes: { [key]: value } }),
    }).then(async (r) => {
      const out = await r.json();
      setMessage(r.ok ? `${key} corrected and recorded in the audit trail`
                      : (typeof out.detail === "string" ? out.detail : "Correction refused"));
      if (r.ok) open(impact.ci.id);
    });

  const open = (id) =>
    apiFetch(`/cmdb/cis/${id}/impact`).then(async (r) => {
      if (!r.ok) { setError("That configuration item is not available to you"); return; }
      setError(null);
      setImpact(await r.json());
    });

  const shown = cis.filter((ci) =>
    `${ci.name} ${ci.number} ${ci.ci_class_key}`.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 40);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="ci-title">CI Explorer</h1>
          <p className="text-sm text-muted-foreground">
            How services, devices and sites connect — so the impact of a change is obvious first.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="ci-changes-link">
            <Link to="/changes">Changes</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {error && <p className="text-sm text-destructive" data-testid="ci-error">{error}</p>}
        {message && <p className="text-sm text-amber-800" data-testid="ci-message">{message}</p>}

        {classes?.can_author && (
          <section className="space-y-3" data-testid="ci-author">
            <h2 className="text-base md:text-lg font-medium">Add a configuration item</h2>
            <p className="text-sm text-muted-foreground" data-testid="ci-author-note">
              {classes.note}
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="ci-class" className="text-sm font-medium">Class</label>
                <select id="ci-class" data-testid="ci-author-class" value={draft.ci_class_key}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        onChange={(e) => setDraft({ ci_class_key: e.target.value, name: "",
                                                    attributes: {} })}>
                  {classes.classes.map((c) => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label htmlFor="ci-name" className="text-sm font-medium">Name</label>
                <Input id="ci-name" data-testid="ci-author-name" value={draft.name}
                       onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              {(chosenClass?.attributes || []).map((a) => (
                <div key={a.key} className="space-y-1">
                  <label htmlFor={`ci-attr-${a.key}`} className="text-sm font-medium">
                    {a.label || a.key}
                    {a.required && <span aria-hidden="true"> *</span>}
                    {chosenClass.identifier_attributes.includes(a.key) && (
                      <span className="text-xs text-muted-foreground"> (identifies the item)</span>
                    )}
                  </label>
                  <Input id={`ci-attr-${a.key}`} data-testid={`ci-author-attr-${a.key}`}
                         type={a.type === "number" ? "number" : a.type === "date" ? "date" : "text"}
                         value={draft.attributes[a.key] ?? ""}
                         onChange={(e) => setDraft({ ...draft,
                                                     attributes: { ...draft.attributes,
                                                                   [a.key]: a.type === "number"
                                                                     ? Number(e.target.value)
                                                                     : e.target.value } })} />
                </div>
              ))}
            </div>
            <Button size="sm" data-testid="ci-author-submit" disabled={!draft.name}
                    onClick={authorCi}>
              Record this item
            </Button>
          </section>
        )}

        <section className="space-y-3" data-testid="ci-list">
          <div className="space-y-1">
            <label htmlFor="ci-search" className="text-sm font-medium">
              Find a configuration item
            </label>
            <Input id="ci-search" data-testid="ci-search" value={search}
                   placeholder="Server, switch, application…"
                   onChange={(e) => setSearch(e.target.value)} />
          </div>
          <ul className="space-y-1">
            {shown.map((ci) => (
              <li key={ci.id}>
                <button className="w-full text-left text-sm border rounded px-3 py-2 hover:border-foreground/40"
                        data-testid={`ci-row-${ci.number || ci.id}`} onClick={() => open(ci.id)}>
                  <span className="font-mono">{ci.number}</span> · {ci.name} ·{" "}
                  <span className="text-muted-foreground">{ci.ci_class_key}</span>
                  {ci.certification_status === "uncertified" && (
                    <span className="text-amber-800"> · uncertified</span>
                  )}
                </button>
              </li>
            ))}
            {cis.length === 0 && !error && (
              <li className="text-sm text-muted-foreground" data-testid="ci-list-empty">
                No configuration items are recorded yet.
              </li>
            )}
          </ul>
        </section>

        {impact && (
          <section className="space-y-4 border-t pt-6" data-testid="ci-impact">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base md:text-lg font-medium" data-testid="ci-impact-title">
                  {impact.ci.name}
                </h2>
                <p className="text-xs text-muted-foreground">
                  <span className="font-mono">{impact.ci.number}</span> ·{" "}
                  {impact.ci.ci_class_key} · {impact.ci.certification_status}
                </p>
              </div>
              <Button size="sm" variant="ghost" data-testid="ci-impact-close"
                      onClick={() => setImpact(null)}>Close</Button>
            </div>

            <section className="grid gap-3 md:grid-cols-3 text-sm"
                     data-testid="ci-impact-summary">
              {[["Related records", impact.impact_summary.relationships, "relationships"],
                ["Devices", impact.impact_summary.devices, "devices"],
                ["Sites", impact.impact_summary.sites, "sites"],
                ["Open outages", impact.impact_summary.open_outages, "outages"],
                ["Open changes", impact.impact_summary.open_changes, "changes"],
                ["Known errors", impact.impact_summary.known_errors, "known-errors"]].map(
                ([label, value, id]) => (
                  <div key={id} className="border rounded-md px-3 py-2"
                       data-testid={`ci-summary-${id}`}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-semibold">{value}</p>
                  </div>
                ))}
            </section>

            <p className="text-sm text-muted-foreground" data-testid="ci-impact-method">
              {impact.definition} Method: {impact.method}
            </p>

            {classes?.can_author && (
              <div className="border rounded-md p-4 space-y-2" data-testid="ci-correct">
                <h3 className="text-sm font-semibold">Correct its attributes</h3>
                <p className="text-xs text-muted-foreground">
                  Every correction is written to the audit trail with your name against it.
                </p>
                <div className="grid gap-2 md:grid-cols-2">
                  {Object.entries(impact.ci.attributes || {}).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <label htmlFor={`ci-fix-${key}`} className="text-xs">{key}</label>
                      <Input id={`ci-fix-${key}`} data-testid={`ci-correct-${key}`}
                             defaultValue={value ?? ""}
                             onBlur={(e) => {
                               if (String(value ?? "") === e.target.value) return;
                               correct(key, e.target.value);
                             }} />
                    </div>
                  ))}
                  {Object.keys(impact.ci.attributes || {}).length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="ci-correct-empty">
                      No attributes are recorded on this item.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h3 className="text-sm font-semibold">Connected to</h3>
                <ul className="text-sm space-y-1" data-testid="ci-relationships">
                  {impact.relationships.map((r) => (
                    <li key={r.id}>
                      {r.label}: {r.other_label}{" "}
                      <span className="text-muted-foreground">({r.other_type})</span>
                    </li>
                  ))}
                  {impact.relationships.length === 0 && (
                    <li className="text-muted-foreground" data-testid="ci-relationships-empty">
                      Nothing you may see is connected to this item.
                    </li>
                  )}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold">Devices pointing at it</h3>
                <ul className="text-sm space-y-1" data-testid="ci-assets">
                  {impact.assets.map((a) => (
                    <li key={a.asset_tag}>
                      <span className="font-mono">{a.asset_tag}</span> — {a.status}
                      {a.holder && `, held by ${a.holder}`}
                      {a.location_key && `, ${a.location_key}`}
                    </li>
                  ))}
                  {impact.assets.length === 0 && (
                    <li className="text-muted-foreground" data-testid="ci-assets-empty">
                      No devices are recorded against it.
                    </li>
                  )}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold">Open work on it</h3>
                <ul className="text-sm space-y-1" data-testid="ci-open-work">
                  {impact.open_outages.map((o) => (
                    <li key={o.number}>Outage {o.number}: {o.title}</li>
                  ))}
                  {impact.open_changes.map((c) => (
                    <li key={c.number}>Change {c.number}: {c.title} ({c.state})</li>
                  ))}
                  {impact.open_outages.length === 0 && impact.open_changes.length === 0 && (
                    <li className="text-muted-foreground" data-testid="ci-open-work-empty">
                      No open outages or changes.
                    </li>
                  )}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold">Problems and known errors</h3>
                <ul className="text-sm space-y-1" data-testid="ci-problems">
                  {impact.problems.map((p) => (
                    <li key={p.number}>
                      {p.number}: {p.title}
                      {p.known_error && " (known error)"}
                    </li>
                  ))}
                  {impact.problems.length === 0 && (
                    <li className="text-muted-foreground" data-testid="ci-problems-empty">
                      None recorded.
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <Button asChild size="sm" data-testid="ci-raise-change">
              <Link to="/changes">Raise a change for this</Link>
            </Button>
          </section>
        )}
      </main>
    </div>
  );
}
